import { useState } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Button from "@/v2-backoffice-client/components/button"
import UserHubspotButton from "@/v2-backoffice-client/user/UserHubspotButton"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import { useUserContactSyncMutation } from "@/v2-backoffice-client/user/contact/sync/user.contact.sync.mutation"
import type { UserContactSyncMutation } from "@/v2-backoffice-client/user/contact/sync/__types__/user.contact.sync.mutation"

type SyncedUser = UserContactSyncMutation["syncUserContact"]

export type UserContactSyncDialogProps = DialogProps & {
  userId: number
  email?: string | null
}

// Operator-triggered HubSpot sync for one user. This is deliberately the
// only place a HubSpot record can be created from the app — the hourly
// job just refreshes properties/snapshots of already-linked contacts —
// so the dialog spells out exactly what will happen before the click.
function UserContactSyncDialog(props: UserContactSyncDialogProps) {
  const { userId, email, open, onOpenChange, ...dialogProps } = props
  const [result, setResult] = useState<SyncedUser | null>(null)

  const [syncContact, { loading, error, reset }] = useUserContactSyncMutation()

  function handleOpenChange(next: boolean) {
    if (!next) {
      setResult(null)
      reset()
    }
    onOpenChange?.(next)
  }

  async function submitSync() {
    const res = await syncContact({ variables: { userId } })
    const synced = res.data?.syncUserContact
    if (synced) setResult(synced)
  }

  const contact = result?.Contact

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        {!result && (
          <>
            <Dialog.Header>
              <Dialog.Title>Sync to HubSpot</Dialog.Title>
              <Dialog.Description>
                Links this user to the HubSpot contact matching{" "}
                {email ? <strong>{email}</strong> : "their email"} — or creates
                a new contact in HubSpot if none exists — and pushes the
                Placepoint properties (status, teams, usage) to it. The CRM
                keeps ownership of everything else on the contact.
              </Dialog.Description>
            </Dialog.Header>

            <ErrorMessage className="my-4 text-center" error={error} />

            <Dialog.Footer>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  handleOpenChange(false)
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                isLoading={loading}
                onClick={async () => {
                  await submitSync()
                }}
              >
                Sync to HubSpot
              </Button>
            </Dialog.Footer>
          </>
        )}

        {result && (
          <>
            <Dialog.Header>
              <Dialog.Title>Contact synced</Dialog.Title>
            </Dialog.Header>

            <div className="py-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium">
                  {formatUserName(contact?.FirstName, contact?.LastName)}
                </span>
                <UserHubspotButton
                  externalContactId={contact?.ExternalContactId}
                />
              </div>
              <div className="text-muted-foreground">{contact?.Email}</div>
              <div className="text-xs text-muted-foreground">
                Contact ID: {contact?.ExternalContactId}
              </div>
            </div>

            <Dialog.Footer>
              <Button
                type="button"
                onClick={() => {
                  handleOpenChange(false)
                }}
              >
                Done
              </Button>
            </Dialog.Footer>
          </>
        )}
      </Dialog.Content>
    </Dialog>
  )
}

export default UserContactSyncDialog
